import { inject, Injectable } from '@angular/core';
import { ACQUISITION_SOURCES, ROUTES } from '@core/constants';

import { ApiService } from '../api/api.service';
import {
  CapturedPhoto,
  CustomerData,
  VehicleData,
  BrandOption,
  ModelOption,
  ServiceOption,
  PlateLookupResponse,
  DocumentTypeOption,
  CreateCustomerPayload,
  UpdateCustomerPayload,
  CreateVehiclePayload,
  CreateReceptionOrderPayload,
  AcquisitionSourceOption,
  IResponseHttp,
  ServiceOrderEvidencesPayload,
  VersionOption,
  YearsAndEnginesCCOption,
} from '@core/models';
import { LookupService } from '../lookup/lookup.service';
import { UploadsService } from '../uploads/uploads.service';
import { CreateVersionVehiclePayload, EvidenceType, unwrapResponse } from '@core/index';

@Injectable({
  providedIn: 'root',
})
export class ReceptionService {
  private readonly api: ApiService = inject(ApiService);
  private readonly uploadsService: UploadsService = inject(UploadsService);
  private readonly lookupService: LookupService = inject(LookupService);

  constructor() {}

  async lookupByPlate(plate: string): Promise<PlateLookupResponse> {
    const normalizedPlate = this.normalizePlate(plate);

    const result = await this.api.get<IResponseHttp>(
      ROUTES.OPS_RECEPTION_PLATE_LOOKUP(normalizedPlate),
    );
    if (result.status !== 'ok' || !result.payload || result.payload.length === 0) {
      return { exists: false, customer: null, vehicle: null };
    }
    const data = result.payload[0];
    const customerData = data.customer;
    const customer: CustomerData = {
      id: customerData.id,
      documentType: customerData.documentType,
      documentNumber: customerData.document,
      fullName: customerData.name,
      phone: customerData.phone,
      email: customerData.email,
      address: customerData.address,
      acquisitionSource: customerData.acquisitionSource,
    };
    const vehicle: VehicleData = {
      id: data.id,
      plate: data.plate,
      brandId: data.brand.id,
      brandName: data.brand.name,
      modelId: data.model.id,
      modelName: data.model.name,
      year: data.version.year,
      yearId: data.version.yearId,
      engineCc: data.version.engineCc,
      engineCcId: data.version.engineCcId,
      vehiculeVersionId: data.version.id,
    };
    return {
      exists: true,
      customer,
      vehicle,
    };
  }

  async lookupCustomerByDocument(
    documentType: DocumentTypeOption['code'],
    document: string,
  ): Promise<CustomerData | null> {
    const result = await this.api.post<IResponseHttp | null>(
      ROUTES.OPS_RECEPTION_DOCUMENT_LOOKUP,
      { documentType, document },
    );
    if (
      !result ||
      result.status !== 'ok' ||
      !result.payload ||
      result.payload.length === 0
    ) {
      return null;
    }

    const data = result.payload[0];
    return {
      id: data.id,
      documentType: data.documentType,
      documentNumber: data.document,
      fullName: data.name,
      phone: data.phone,
      email: data.email,
      address: data.address,
      acquisitionSource: data.acquisitionSource,
    };
  }

  async createCustomer(payload: CreateCustomerPayload): Promise<CustomerData> {
    const newPayload = {
      ...payload,
      phone: payload.phone.toString(),
    };
    const result = await this.api.post<IResponseHttp>(
      ROUTES.OPS_RECEPTION_CREATE_CUSTOMER,
      newPayload,
    );

    const data = result.payload;
    return {
      id: data.id,
      documentType: data.documentType,
      documentNumber: data.document,
      fullName: data.fullName,
      phone: data.phone,
      email: data.email,
      address: data.address,
      acquisitionSource: data.acquisitionSource,
    };
  }

  async updateCustomer(
    customerId: string,
    payload: UpdateCustomerPayload,
  ): Promise<CustomerData> {
    const result = await this.api.patch<IResponseHttp>(
      ROUTES.OPS_RECEPTION_UPDATE_CUSTOMER(customerId),
      payload,
    );
    const data = result.payload;
    return {
      id: data.id,
      documentType: data.documentType,
      documentNumber: data.document,
      fullName: data.name,
      phone: data.phone,
      email: data.email,
      address: data.address,
      acquisitionSource: data.acquisitionSource,
    };
  }

  async getBrands(): Promise<BrandOption[]> {
    return await this.lookupService.getBrandsCars();
  }

  async getYearsAndEnginesCC(): Promise<YearsAndEnginesCCOption> {
    return await this.lookupService.getYearsAndEnginesCC();
  }

  async getModelsByBrand(brandId: string): Promise<ModelOption[]> {
    const result = await this.lookupService.getModelsCars(brandId);
    return result;
  }

  async getVersionsByModel(modelId: string): Promise<VersionOption[]> {
    const result = await this.api.get<IResponseHttp>(
      ROUTES.LOOKUP_CARS_VERSIONS_BY_MODEL(modelId),
    );
    if (!result.payload && result.payload.length === 0) {
      return [];
    }
    return result.payload as VersionOption[];
  }

  async createVehicle(payload: CreateVehiclePayload): Promise<VehicleData> {
    const result = await this.api.post<IResponseHttp>(
      ROUTES.OPS_RECEPTION_CREATE_VEHICLE,
      {
        modelId: payload.modelId,
        yearId: payload.yearId,
        engineCcId: payload.engineCcId,
        plate: payload.plate,
        customerId: payload.customerId,
      },
    );
    return {
      id: result.payload.id,
      plate: result.payload.plate,
      brandId: result.payload.brand.id,
      brandName: result.payload.brand.name,
      modelId: result.payload.model.id,
      modelName: result.payload.model.name,
      year: result.payload.version.year,
      yearId: result.payload.version.yearId,
      engineCc: result.payload.version.engineCc,
      engineCcId: result.payload.version.engineCcId,
      vehiculeVersionId: result.payload.version.id,
    };
  }

  async createVersionVehicle(payload: CreateVersionVehiclePayload): Promise<string> {
    const result = await this.api.post<IResponseHttp>(
      ROUTES.OPS_RECEPTION_CREATE_VERSION_VEHICLE,
      payload,
    );
    return result.payload.vehicleVersionId as string;
  }

  async searchServices(vehicleVersionId: string): Promise<ServiceOption[]> {
    const result = await this.api.get<IResponseHttp>(
      ROUTES.OPS_RECEPTION_SERVICES_SEARCH(vehicleVersionId),
    );
    if (result.payload.length === 0) {
      return [];
    }
    return result.payload;
  }

  async createReceptionOrder(
    payload: CreateReceptionOrderPayload,
  ): Promise<{ orderId: string }> {
    const response = await this.api.post<
      IResponseHttp | { id?: string; orderId?: string }
    >(ROUTES.OPS_RECEPTION_CREATE_ORDER, payload);
    const data = unwrapResponse<{ id?: string; orderId?: string }>(response);
    const orderId = data.orderId ?? data.id;

    if (!orderId) {
      throw new Error('No se recibio el id de la orden creada.');
    }

    return { orderId };
  }

  async cancelReceptionOrder(orderId: string): Promise<void> {
    await this.api.delete<IResponseHttp>(ROUTES.OPS_RECEPTION_CANCEL_ORDER(orderId));
  }

  async uploadReceptionEvidences(
    orderId: string,
    photos: CapturedPhoto[],
  ): Promise<string[]> {
    const presigned = await this.uploadsService.getPresignedUrls({
      orderId,
      type: EvidenceType.RECEPTION,
      files: photos.map((photo) => ({ fileName: photo.fileName })),
    });

    if (presigned.length !== photos.length) {
      throw new Error('No se recibieron URLs firmadas para todas las fotos.');
    }

    const uploadedUrls: string[] = await this.uploadsService.uploadReceptionEvidences(
      presigned,
      photos,
    );

    await this.addServiceOrderEvidences(orderId, {
      type: EvidenceType.RECEPTION,
      urls: uploadedUrls,
    });

    return uploadedUrls;
  }

  async uploadReceptionSignature(
    orderId: string,
    signatureDataUrl: string,
  ): Promise<string[]> {
    const signature = this.dataUrlToCapturedPhoto(signatureDataUrl);
    const photos = [signature];

    try {
      const presigned = await this.uploadsService.getPresignedUrls({
        orderId,
        type: EvidenceType.SIGNATURE,
        files: photos.map((photo) => ({ fileName: photo.fileName })),
      });

      if (presigned.length !== photos.length) {
        throw new Error('No se recibio URL firmada para la firma del cliente.');
      }

      const uploadedUrls = await this.uploadsService.uploadReceptionEvidences(
        presigned,
        photos,
      );

      await this.addServiceOrderEvidences(orderId, {
        type: EvidenceType.SIGNATURE,
        urls: uploadedUrls,
      });

      return uploadedUrls;
    } finally {
      URL.revokeObjectURL(signature.previewUrl);
    }
  }

  async getDocumentTypeOptions(): Promise<DocumentTypeOption[]> {
    return (await this.lookupService.getDocumentType()) as DocumentTypeOption[];
  }

  getAcquisitionSourceOptions(): AcquisitionSourceOption[] {
    return ACQUISITION_SOURCES;
  }

  private normalizePlate(plate: string): string {
    return plate.toUpperCase().replace(/\s+/g, '').trim();
  }

  private dataUrlToCapturedPhoto(dataUrl: string): CapturedPhoto {
    const [header, base64] = dataUrl.split(',');
    if (!header || !base64) {
      throw new Error('Firma inválida.');
    }

    const mimeMatch = header.match(/data:(.*?);base64/);
    const mimeType = mimeMatch?.[1] ?? 'image/png';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    const fileName = `signature-${Date.now()}.png`;
    const file = new File([bytes], fileName, { type: mimeType });
    return {
      fileName,
      file,
      previewUrl: URL.createObjectURL(file),
    };
  }

  private async addServiceOrderEvidences(
    orderId: string,
    payload: ServiceOrderEvidencesPayload,
  ): Promise<void> {
    await this.api.patch<IResponseHttp | unknown>(
      ROUTES.OPS_WORK_ORDER_ADD_EVIDENCES(orderId),
      payload,
    );
  }
}
